import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardMetrics, exportSummary, exportAllBanks, getAllBanks } from '../services/api';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Download, TrendingUp, Building2, CheckCircle, AlertCircle, Trash2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney } from '../utils/bankConstants';

import KpiDetailPopup from '../components/dashboard/KpiDetailPopup';
import VendorSummaryTable from '../components/dashboard/VendorSummaryTable';
import AgentPerformanceTable from '../components/dashboard/AgentPerformanceTable';
import WipeDatabaseDialog from '../components/dashboard/WipeDatabaseDialog';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  const isSpecificAgent = user?.role === 'Agent' && user?.accessLevel === 'specific';

  const [kpiPopup, setKpiPopup] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [allBanksCache, setAllBanksCache] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await getDashboardMetrics();
      setMetrics(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'Partner') { navigate('/my-banks', { replace: true }); return; }
    fetchMetrics();
  }, [user, navigate, fetchMetrics]);

  const handleExportSummary = async () => {
    try {
      const response = await exportSummary();
      downloadCSV(response.data.csv, response.data.filename);
      toast.success('Summary exported successfully');
    } catch { toast.error('Failed to export summary'); }
  };

  const handleExportAllBanks = async () => {
    try {
      const response = await exportAllBanks();
      downloadCSV(response.data.csv, response.data.filename);
      toast.success('Banks exported successfully');
    } catch { toast.error('Failed to export banks'); }
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename;
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  const handleKpiClick = async (title, filterFn) => {
    setKpiLoading(true);
    setKpiPopup({ title, banks: [] });
    try {
      let banks = allBanksCache;
      if (!banks) {
        const res = await getAllBanks();
        banks = res.data;
        setAllBanksCache(banks);
      }
      setKpiPopup({ title, banks: filterFn(banks) });
    } catch {
      toast.error('Failed to load details');
      setKpiPopup(null);
    }
    setKpiLoading(false);
  };

  const getAccountTypeBreakdown = (statusFilter) => {
    if (!metrics?.vendorSummary) return [];
    const counts = {};
    metrics.vendorSummary.forEach(v => {
      (v.accountTypesPerStatus || []).forEach(item => {
        if (statusFilter === 'all' || item.status === statusFilter || (statusFilter === 'freezed' && item.status?.startsWith('Freezed'))) {
          counts[item.type] = (counts[item.type] || 0) + item.count;
        }
      });
    });
    return Object.entries(counts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);
  };

  const AccountTypeBadges = ({ statusFilter }) => {
    const entries = getAccountTypeBreakdown(statusFilter);
    if (entries.length === 0) return null;
    return (
      <div className="flex gap-1 flex-wrap mt-2">
        {entries.map(([type, count]) => (
          <span key={type} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{type}: {count}</span>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-lg">Loading dashboard...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <MobileNavbar />
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4 md:space-y-6 mobile-content-wrapper">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
            <p className="text-slate-600 mt-1 text-sm md:text-base">Overview of vendor and bank metrics</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleExportSummary} variant="outline" className="gap-2 flex-1 md:flex-none">
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export Summary</span>
              <span className="md:hidden">Summary</span>
            </Button>
            <Button onClick={handleExportAllBanks} variant="outline" className="gap-2 flex-1 md:flex-none">
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export All Banks</span>
              <span className="md:hidden">All Banks</span>
            </Button>
            {user?.role === 'Super Admin' && (
              <Button onClick={() => setShowWipeDialog(true)} variant="destructive" className="gap-2 flex-1 md:flex-none" data-testid="wipe-database-btn">
                <Trash2 className="w-4 h-4" />
                <span className="hidden md:inline">Wipe Database</span>
                <span className="md:hidden">Wipe DB</span>
              </Button>
            )}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('All Vendors', (banks) => banks)} data-testid="kpi-total-vendors">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><Building2 className="w-4 h-4" />{isSpecificAgent ? 'Assigned Vendors' : 'Total Vendors'}</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{metrics?.totalVendors || 0}</div><p className="text-xs text-slate-500 mt-1">Click to view all banks</p></CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('All Banks', (banks) => banks)} data-testid="kpi-total-banks">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><Building2 className="w-4 h-4" />{isSpecificAgent ? 'Assigned Banks' : 'Total Banks'}</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{metrics?.totalBanks || 0}</div><AccountTypeBadges statusFilter="all" /></CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('Active Banks', (banks) => banks.filter(b => b.status === 'Active'))} data-testid="kpi-active-banks">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Active Banks</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-green-600">{metrics?.activeBanks || 0}</div><AccountTypeBadges statusFilter="Active" /></CardContent>
          </Card>

          <Card className="border-l-4 border-l-teal-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('Unassigned Banks', (banks) => banks.filter(b => b.status === 'Unassigned'))} data-testid="kpi-unassigned">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><CheckCircle className="w-4 h-4" />Unassigned</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-teal-600" data-testid="unassigned-count">{metrics?.unassigned || 0}</div><AccountTypeBadges statusFilter="Unassigned" /></CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('In Process Banks', (banks) => banks.filter(b => b.status === 'In Process'))} data-testid="kpi-in-process">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><TrendingUp className="w-4 h-4" />In Process</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-amber-600">{metrics?.inProcess || 0}</div><AccountTypeBadges statusFilter="In Process" /></CardContent>
          </Card>

          <Card className="border-l-4 border-l-indigo-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('Assigned Banks', (banks) => banks.filter(b => b.assignedPartnerId))} data-testid="kpi-assigned-banks">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><UserCheck className="w-4 h-4" />Assigned</CardTitle></CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-indigo-600">{metrics?.assignedBanks || 0}</div>
              {metrics?.assignedPartnerBreakdown?.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {metrics.assignedPartnerBreakdown.map((p) => (
                    <span key={p.partnerId} className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100"
                      onClick={(e) => { e.stopPropagation(); handleKpiClick(`Assigned to ${p.partnerName}`, (banks) => banks.filter(b => b.assignedPartnerId === p.partnerId)); }}>
                      {p.partnerName}: {p.count}
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Freeze Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('Freezed Banks', (banks) => banks.filter(b => b.status?.startsWith('Freezed')))} data-testid="kpi-freezed">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2"><AlertCircle className="w-4 h-4" />Freezed Banks</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-red-600">{metrics?.freezedBanks || 0}</div><AccountTypeBadges statusFilter="freezed" /></CardContent>
          </Card>
          <Card className="md:col-span-2 border-l-4 border-l-red-500 cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleKpiClick('Freezed Banks (Amount)', (banks) => banks.filter(b => b.status?.startsWith('Freezed')))} data-testid="kpi-freezed-amount">
            <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Total Freezed Amount</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-red-600">{formatMoney(metrics?.freezedAmount || 0)}</div><p className="text-xs text-slate-500 mt-1">Click for details</p></CardContent>
          </Card>
        </div>

        {/* Account Type & QR Summary */}
        {metrics?.vendorSummary?.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">Account Type Breakdown</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-2 flex-wrap">
                  {(() => {
                    const typeCounts = {};
                    metrics.vendorSummary.forEach(v => { (v.accountTypes || []).forEach(at => { typeCounts[at.type] = (typeCounts[at.type] || 0) + at.count; }); });
                    const entries = Object.entries(typeCounts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);
                    if (entries.length === 0) return <span className="text-sm text-slate-400">No data</span>;
                    return entries.map(([type, count]) => (
                      <Badge key={type} variant="outline" className="text-sm px-3 py-1.5 cursor-pointer hover:bg-slate-100"
                        onClick={() => handleKpiClick(`${type} Accounts`, (banks) => banks.filter(b => (b.accountType || 'Savings') === type))} data-testid={`account-type-${type}`}>
                        {type}: <strong className="ml-1">{count}</strong>
                      </Badge>
                    ));
                  })()}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-sm font-medium text-slate-600">QR Availability</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  {(() => {
                    let withQr = 0, withoutQr = 0;
                    metrics.vendorSummary.forEach(v => { withQr += (v.withQr || 0); withoutQr += (v.withoutQr || 0); });
                    return (
                      <>
                        <Badge className="text-sm px-3 py-1.5 bg-green-100 text-green-800 border-green-200 cursor-pointer hover:bg-green-200"
                          onClick={() => handleKpiClick('Banks With QR', (banks) => banks.filter(b => b.qrAvailable === 'yes'))} data-testid="kpi-with-qr">
                          With QR: <strong className="ml-1">{withQr}</strong>
                        </Badge>
                        <Badge variant="outline" className="text-sm px-3 py-1.5 cursor-pointer hover:bg-slate-100"
                          onClick={() => handleKpiClick('Banks Without QR', (banks) => banks.filter(b => b.qrAvailable !== 'yes'))} data-testid="kpi-without-qr">
                          Without QR: <strong className="ml-1">{withoutQr}</strong>
                        </Badge>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <VendorSummaryTable metrics={metrics} formatMoney={formatMoney} handleKpiClick={handleKpiClick} />
        {user?.role !== 'Agent' && <AgentPerformanceTable metrics={metrics} formatMoney={formatMoney} />}
      </div>

      <KpiDetailPopup kpiPopup={kpiPopup} kpiLoading={kpiLoading} onClose={() => setKpiPopup(null)} />

      <WipeDatabaseDialog open={showWipeDialog} onOpenChange={setShowWipeDialog} onWiped={fetchMetrics} />
    </>
  );
};

export default DashboardPage;
